<script setup lang="ts">
import { useDivisibleReceiver } from '@precon-tools-suite-by-shared/vue-divisibles';
import { usePartialComponent } from '@precon-tools-suite-by-shared/vue-partials';
import type { Component } from 'vue';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';
import Panel from 'primevue/panel';

const props = withDefaults(
    defineProps<{
        partial: Component | string;
        fallbackPartial?: Component | string | null;
        partialProps?: Record<string, unknown>;
    }>(),
    {
        fallbackPartial: null,
        partialProps: () => ({}),
    }
);

const receiver = useDivisibleReceiver();
const partialComponent = usePartialComponent(() => props.partial, {
    fallback: () => props.fallbackPartial,
});

const { component: HeaderPart, exists: headerExists } = receiver.use('header');
const { component: TitlePart, exists: titleExists } = receiver.use('title');
const { component: SubtitlePart, exists: subtitleExists } =
    receiver.use('subtitle');
const { component: ToolsPart, exists: toolsExists } = receiver.use('tools');
</script>

<template>
    <Panel :toggleable="true">
        <template #header>
            <HeaderPart v-if="headerExists" />
            <template v-else>
                <span class="p-panel-title">
                    <TitlePart v-if="titleExists">
                        <template #skeleton>
                            <Skeleton
                                style="
                                    width: 6rem;
                                    height: 1.1rem;
                                    display: inline-block;
                                "
                            />
                        </template>
                    </TitlePart>
                    <span
                        v-if="subtitleExists"
                        style="
                            font-weight: normal;
                            font-size: smaller;
                            opacity: 0.6;
                        "
                        class="ml-1"
                    >
                        <SubtitlePart>
                            <template #skeleton>
                                <Skeleton
                                    style="
                                        width: 8rem;
                                        height: 0.9rem;
                                        display: inline-block;
                                    "
                                />
                            </template>
                        </SubtitlePart>
                    </span>
                </span>
            </template>
        </template>

        <template #icons>
            <ToolsPart v-if="toolsExists" button-class="p-panel-header-icon">
                <template #skeleton>
                    <Button
                        class="p-button-rounded p-button-text p-button-secondary p-panel-header-icon"
                        icon="pi pi-spinner pi-spin"
                        disabled
                    />
                </template>
            </ToolsPart>
        </template>

        <component :is="partialComponent" v-bind="partialProps">
            <slot />
        </component>
    </Panel>
</template>
